import React from 'react';

import Link from '../../../../library/link/link';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import ProductImage from '../../../../components/product-image/product-image';
import WebOnlyFlag from '../../../../components/web-only-flag/web-only-flag';
import ProductListPackageCardHeader from './components/product-list-package-card-header/product-list-package-card-header';
import { WhiteAngleRegularIcon } from '../../../../shared/icons/icons';
import ProductListCardActions from '../product-list-card-actions/product-list-card-actions';
import ProductListCardDetails from '../product-list-card-details/product-list-card-details';
import ProductListCardHighlight from '../product-list-card-highlight/product-list-card-highlight';
import ProductListCardPrice from '../product-list-card-price/product-list-card-price';

import {
  useMediaQuery,
  useOptimisticData,
  useProductQuantity
} from '../../../../shared/hooks/hooks';

import {
  getShouldShowMinimumPriceMessage,
  isWebOnlyProduct
} from '../../../../shared/utils/product/product';

import { BundleType } from '../../../../shared/types/graphql-types';
import { BundleData, ProductItem } from '../../../../shared/types/product';

import styles from './product-list-package-card.scss';

interface ProductListPackageCardProps {
  onAddProductClick: (productItem: ProductItem) => void;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  product: BundleData;
}

function ProductListPackageCard(props: ProductListPackageCardProps) {
  const {
    onAddProductClick,
    onCheckInventoryClick,
    onViewProductClick,
    product: prebuiltPackage
  } = props;
  const { isMinSm } = useMediaQuery();

  const { product1, product2, url: productDetailUrl = '' } = prebuiltPackage;
  const { defaultQuantity, quantity, setQuantity, totalQuantity } =
    useProductQuantity({
      bundleType: BundleType.TireWheelPackage
    });

  const optimisticData = useOptimisticData();

  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product1);

  const isWebOnly = isWebOnlyProduct(product1);

  if (!product1 || !product2) return null;

  if (window['isPreRendering']) {
    return <div className={styles['container']} />;
  }

  function setOptimisticProductData() {
    optimisticData.write({ prebuiltPackage });
    onViewProductClick?.();
  }

  return (
    <div
      className={styles['container']}
      data-testid="product-list-package-card"
      id={product1 && product1.code ? `product-code-${product1.code}` : ''}
    >
      {(process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true' || isMinSm) && (
        <div className={styles['img-col']}>
          <ProductListCardHighlight
            hideTreadwellRank
            hideWebOnly
            product={product1}
          />

          <Link
            className={styles['images']}
            onClick={setOptimisticProductData}
            to={productDetailUrl}
          >
            <ProductImage
              className={styles['tire-img']}
              height={190}
              product={product1}
              width={190}
            />
            <ProductImage
              className={styles['wheel-img']}
              height={190}
              product={product2}
              width={190}
            />
          </Link>

          {isWebOnly && (
            <WebOnlyFlag className="product-list-package-card__web-only-flag" />
          )}

          <WhiteAngleRegularIcon className={styles['icon']} />
        </div>
      )}

      {process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' && !isMinSm && (
        <ProductListCardHighlight
          hideTreadwellRank
          hideWebOnly
          product={product1}
        />
      )}

      <ProductListPackageCardHeader
        hideAvailability={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
        prebuiltPackage={prebuiltPackage}
        quantity={quantity}
      />

      <ProductListCardDetails
        header="Tire:"
        hideAvailability
        hideCompare
        hideHighlight
        hideImage={process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true' || isMinSm}
        onCheckInventoryClick={onCheckInventoryClick}
        onViewProductClick={onViewProductClick}
        product={product1}
        productDetailUrl={productDetailUrl}
        quantity={quantity}
      />

      <div className={styles['product-one-price']}>
        <ProductListCardPrice
          hideFoundItLower
          hidePercentageSaved
          isPrebuiltPackage
          product={product1}
          quantity={quantity}
          showStaticQuantity={Boolean(prebuiltPackage)}
          totalQuantity={totalQuantity}
        />
        {showMinimumPriceMessage && (
          <MinimumPriceMessage message={product1?.mapDisplayRule?.message} />
        )}
      </div>

      <hr className={styles['package-divider']} />

      <div className={styles['wheel-details']}>
        <ProductListCardDetails
          header="Wheel:"
          hideAvailability
          hideHighlight
          hideImage={
            process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true' || isMinSm
          }
          hideViewOnVehicleAddToCart
          hideViewOnVehicleFilters
          onCheckInventoryClick={onCheckInventoryClick}
          onViewProductClick={onViewProductClick}
          product={product2}
          productDetailUrl={productDetailUrl}
          quantity={quantity}
        />
      </div>

      <ProductListCardActions
        defaultQuantity={defaultQuantity}
        hideFoundItLower
        onAddProductClick={onAddProductClick}
        onQuantityChange={setQuantity}
        prebuiltPackage={prebuiltPackage}
        product={product2}
        quantity={quantity}
        showAvailability={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
        totalQuantity={totalQuantity}
      />
    </div>
  );
}

export default ProductListPackageCard;
